import { supabase } from '@/integrations/supabase/client';
import { parseCsvLine, toCsvContent } from '@/lib/csv';
import { Product } from '../types';

export interface ProcessResult {
  processed: number;
  successful: number;
  failed: number;
  errors: string[];
}

export const inventoryApi = {
  /**
   * Upload a product image to storage
   */
  async uploadProductImage(file: File, sku: string): Promise<string> {
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const safeFileName = `${sku.replace(/[^a-zA-Z0-9]/g, '')}_${timestamp}.${fileExtension}`;

    const { error: uploadError } = await supabase.storage
      .from('product_images')
      .upload(safeFileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('product_images')
      .getPublicUrl(safeFileName);

    return publicUrl;
  },

  /**
   * Upload a spec/QC PDF to storage
   */
  async uploadSpecPdf(file: File, productTitle: string): Promise<string> {
    const timestamp = Date.now();
    const safeName = `${productTitle.replace(/[^a-zA-Z0-9]/g, '')}_spec_${timestamp}.pdf`;

    const { error: uploadError } = await supabase.storage
      .from('product_documents')
      .upload(safeName, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('product_documents')
      .getPublicUrl(safeName);

    return publicUrl;
  },

  /**
   * Parse CSV and add stock to products
   */
  async bulkAddStockFromCsv(
    csvFile: File,
    currentProducts: Product[],
    _locationId: string,
    addStockFn: (sku: string, quantity: number) => Promise<boolean>
  ): Promise<ProcessResult> {
    const result: ProcessResult = {
      processed: 0,
      successful: 0,
      failed: 0,
      errors: []
    };

    const text = await csvFile.text();
    const rows = text.split(/\r?\n/);

    if (rows.length < 2) {
      throw new Error("CSV file appears to be empty or invalid");
    }

    const headerRow = rows[0];
    if (!headerRow) throw new Error("CSV file has no header row");

    const headers = parseCsvLine(headerRow).map(h => h.trim().toLowerCase());
    const skuColumnIndex = headers.indexOf('sku');
    const stockColumnIndex = headers.indexOf('stock');

    if (skuColumnIndex === -1) {
      throw new Error("CSV file must contain a 'sku' column");
    }

    if (stockColumnIndex === -1) {
      throw new Error("CSV file must contain a 'stock' column");
    }

    const updates = [];

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || !row.trim()) continue;

      const rowData = parseCsvLine(row).map(cell => cell.trim());
      const sku = rowData[skuColumnIndex];
      const stockValueStr = rowData[stockColumnIndex];
      if (!stockValueStr) continue;

      const stockValue = parseInt(stockValueStr, 10);

      result.processed++;

      if (!sku) {
        result.failed++;
        result.errors.push(`Row ${i}: SKU is missing`);
        continue;
      }

      if (isNaN(stockValue)) {
        result.failed++;
        result.errors.push(`Row ${i}: Invalid stock quantity for SKU ${sku}`);
        continue;
      }

      const productToUpdate = currentProducts.find(product => product.sku === sku);
      if (!productToUpdate) {
        result.failed++;
        result.errors.push(`Row ${i}: SKU ${sku} not found in inventory`);
        continue;
      }

      updates.push({ sku, quantity: stockValue });
    }

    // Perform updates
    for (const update of updates) {
      try {
        await addStockFn(update.sku, update.quantity);
        result.successful++;
      } catch (error) {
        result.failed++;
        result.errors.push(
          `Failed to update SKU ${update.sku}: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    }

    return result;
  },

  /**
   * Download inventory as CSV
   */
  downloadStockCsv(products: Product[], locationName: string): void {
    if (products.length === 0) {
      throw new Error("No data to download");
    }

    // Generate CSV content
    const csvContent = toCsvContent([
      ['SKU', 'Title', 'production_type', 'type', 'color', 'size', 'on_hand', 'available', 'committed', 'unavailable', 'incoming', 'current_stock'],
      ...products.map(product => [
        product.sku,
        product.title || '',
        product.productionType,
        product.category.type || '',
        product.category.color || '',
        product.category.size || '',
        product.onHand ?? product.stockLevel,
        product.available ?? product.stockLevel,
        product.committed ?? 0,
        product.unavailable ?? 0,
        product.incoming ?? 0,
        product.stockLevel
      ])
    ]);

    // Create filename
    const locationSlug = locationName.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    const date = new Date().toISOString().split('T')[0];
    const filename = `stock-${locationSlug}-${date}.csv`;

    // Trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }
};
